// Every string the installer portal shows, in English and Simplified Chinese.
// `t(key, vars)` fills `{name}` placeholders. Data that arrives from the API
// (job names, addresses, products, office notes) is shown as sent.
//
// The MECHANISM is @aiw/i18n — the same one the CRM and the consumer web run on
// (board card AIW-CN, 2026-09-16). This file keeps what is the installer's: its
// own keys, its own wording, its own storage key. `Key` is `keyof typeof en` and
// `zh` is typed as a complete `Record<Key, string>`, so a new English string
// without its Chinese is a compile error.
import { createContext, useContext } from "react";
import { translateIn, useLangState as useSharedLangState, type Lang as SharedLang, type Vars } from "@aiw/i18n/react";

export type { Vars };
export type Lang = SharedLang;
const KEY = "aiw.installer.lang";

const en = {
  payments_and_statements: "Payments & statements",
  // Installer fee payments, per Job (25 Oct 2026): what the office paid you, Job by Job.
  jobpay_title: "Paid to you, by Job",
  jobpay_explainer: "Each payment the office recorded for your company, against the Job it paid: cash or bank transfer, the date, and your invoice number if you sent one. Amounts ex GST.",
  jobpay_unavailable: "Your payments are unavailable right now. Refresh or contact the office.",
  jobpay_empty: "No payment has been recorded against a Job yet.",
  jobpay_amount: "{amount} ex GST",
  jobpay_paid_on: "Paid {date}",
  jobpay_invoice: "invoice {number}",
  jobpay_cash: "Cash",
  jobpay_transfer: "Bank transfer",
  recorded_payments: "Recorded payments",
  fees_tab: "Job fees",
  fees_explainer: "The fee the office confirmed for each job: the lines, the total ex GST and how you are paid. It is agreed unless you raise a variation.",
  fees_unavailable: "Job fees are unavailable right now. Refresh or contact the office.",
  fees_empty: "No confirmed job fee yet. The office confirms a fee when a job's work is settled.",
  fee_agreed: "Agreed",
  fee_variation_open: "Variation raised",
  fee_variation_accepted: "Variation accepted — the office is updating the fee",
  fee_variation_declined: "Variation declined",
  fee_total: "Total {amount} ex GST",
  fee_paid_cash: "paid in cash",
  fee_paid_transfer: "paid by bank transfer",
  fee_confirmed_on: "confirmed by the office {date}",
  fee_raise: "Raise a variation",
  fee_raise_help: "Tell the office what is different. The fee stays as confirmed until the office answers you.",
  fee_note: "What is different",
  fee_proposed: "Amount you expect (optional, ex GST)",
  fee_send: "Send to the office",
  fee_sent: "Sent. The office will come back to you here.",
  fee_cancel: "Cancel",
  fee_your_note: "Your note · {date}",
  payments_explainer: "Payments recorded by the office, separate from published statements. No receipt confirmation does not mean a payment is unpaid. These records are not a current balance or a bank-settlement guarantee.",
  payments_refresh: "Refresh records",
  payments_unavailable: "Payment records are unavailable or need office review. Refresh or contact the office. This does not mean nothing is owed.",
  payments_empty: "No payment records are available for your company. This does not establish an amount owed; contact the office if a payment is missing.",
  payment_recorded_date: "Payment recorded · {date}",
  payment_reference: "Reference: {reference}",
  payment_reference_missing: "No reference recorded",
  payment_receipt_confirmed: "Receipt confirmation recorded · {date}",
  payment_receipt_unconfirmed: "No receipt confirmation recorded",
  payment_allocations: "Allocated to jobs",
  payment_no_allocations: "No job allocations recorded",
  payment_unallocated: "Not yet allocated to a job: {amount}",

  // shell
  role_external: "external party",
  role_internal: "internal crew",
  lang_toggle: "中文",
  tab_today: "Today",
  tab_jobs: "My jobs",
  tab_wo: "Work order",
  tab_owed: "Owed",
  tab_owed_external_only: "Owed · external only",
  tab_team: "Team",
  tab_pricing: "Pricing",
  mock_chip: "example data",
  loading: "Loading…",
  signing_in: "Signing you in…",
  not_here: "Not here",
  no_such_page: "No such page.",
  sign_out: "Sign out",
  back: "Back",
  send: "Send",
  open: "open",
  // sign in
  signin_title: "Artemis installer portal",
  signin_body: "Sign in with the email the office has for you. We send a link; tap it on this phone.",
  signin_email: "Email",
  signin_send: "Send me a link",
  signin_sent: "Link sent · check your email on this phone.",
  signin_failed: "Could not send the link: {error}",
  signin_phone_body: "Sign in with the mobile number the office has for you. We text you a code.",
  signin_phone: "Mobile number",
  signin_send_code: "Text me a code",
  signin_code_sent: "Code sent to {phone} · it is good for five minutes.",
  signin_code: "The code from the text",
  signin_verify: "Sign in",
  signin_change_number: "Use a different number",
  signin_welcome: "Signed in. Keeping you signed in on this phone.",
  signin_use_email: "Use an email link instead",
  signin_staff_sso: "Artemis staff · Microsoft sign in",
  signin_staff_sso_note: "For Jacky and Artemis staff. No email link is sent.",
  signin_use_phone: "Use my mobile instead",
  // today
  title_today: "Today",
  sub_today: "{date} · {n} job · day {d} of {m}",
  sub_today_many: "{date} · {n} jobs",
  sub_today_none: "{date} · no job today",
  no_job_today: "No job today",
  work_load_failed: "Could not load your assigned work. Try again.",
  retry_work: "Try again",
  next_job: "Next: {name} · {date}",
  day_n_of_m: "Day {n} of {m}",
  stairs: "stairs",
  start_at: "Start {time}",
  start_time_unset: "Start time to confirm",
  materials_status_unrecorded: "Material delivery status not recorded",
  materials_since: "Materials on site since {date}",
  materials_not_yet: "Materials not on site yet",
  btn_work_order: "Work order",
  assigned_day: "Assigned installation day",
  onsite_access_needed: "This account manages company work. Assign an onsite installer to report completion.",
  btn_set_out: "Set-out check",
  btn_day_complete: "Day complete",
  biglink_complete: "Day complete · tick assigned scope",
  new_job_offered: "New job offered",
  tomorrow: "Tomorrow",
  tomorrow_none: "Nothing tomorrow",
  day_n: "Day {n}",
  // offer card
  offer_from: "Artemis office",
  offer_answer_by: "answer by {when}",
  offer_what: "What will happen",
  offer_why: "Why you",
  offer_options: "Options",
  offer_if_no: "If you say no",
  offer_undo: "Undo",
  undo_reversible: "Can be undone",
  undo_with_effort: "Undo takes effort · call the office",
  undo_irreversible: "Cannot be undone",
  verb_accept: "Accept",
  verb_ask: "Ask…",
  verb_decline: "Decline",
  ask_label: "Ask the office",
  ask_placeholder: "Your question goes to the office; they answer here.",
  decline_label: "Why decline? (optional)",
  decline_placeholder: "A sentence for the office.",
  offer_sending: "Sending…",
  offer_accepted: "Accepted · on your list",
  offer_asked: "Question sent to the office",
  offer_declined: "Declined · the office will offer it to another crew",
  offer_failed: "Could not send · try again",
  offer_expired: "This offer has closed",
  // my jobs
  jobs_scope_external: "You see {party}'s jobs only.",
  jobs_scope_internal: "Team Artemis · every crew job, no rates.",
  group_upcoming: "Upcoming",
  group_completion_to_check: "Completion to check",
  group_done: "Done",
  empty_group: "Nothing here",
  pill_installing: "Installing",
  pill_accepted: "Accepted",
  pill_offered: "Offered · answer by {when}",
  pill_scheduled: "Scheduled",
  pill_done: "Signed off {date}",
  pill_install_complete: "Installation complete",
  pill_completion_to_check: "Check completion",
  pill_day_today: "Day {n} today",
  pill_day_complete: "Day {n} complete",
  // work order
  wo_title: "Work order · {name}",
  wo_no_job: "No work order to show yet.",
  wo_not_published: "Work order details are unavailable. Your assigned site and days are shown below.",
  site: "Site",
  open_maps: "Open in Maps",
  days: "Days",
  parking: "Parking & unloading",
  access: "Access",
  ppe: "PPE",
  office: "Office",
  notes_office: "Notes from the office",
  materials: "Materials for this job",
  supplier_pickups: "Supplier pickups",
  pickup_number: "Show this number at pickup",
  pickup_awaiting_email: "Supplier pickup number awaiting email confirmation. Ask the office before collecting.",
  delivered: "Delivered {date}",
  not_delivered: "Not delivered yet",
  your_labour: "Your labour",
  party_only: "{party} only",
  labour_total: "Total",
  on_statement: "on your statement after sign-off",
  internal_no_rates: "Team Artemis crews do not see rates or amounts anywhere.",
  setout_drawing: "Set-out drawing",
  setout_caption: "plank direction · start row · from the measure {date}",
  start_row: "start row · 10 mm gap",
  days_caption: "Day complete per day, then PM sign-off",
  pm_signoff: "PM sign-off",
  pm_after_all: "after all days",
  pm_detail: "{pm} · after day {n} · photos of every room",
  photos: "Photos",
  camera: "Camera",
  upload: "Upload",
  uploading: "Uploading…",
  photo_prepare_failed: "This photo could not be resized on this phone. Try another photo or camera format.",
  photo_too_large: "This photo could not be reduced below 1 MB. Try another photo.",
  photo_format_failed: "This photo format was rejected. Try a JPEG or a new camera photo.",
  photo_access_failed: "Photo upload access expired. Sign in again and retry.",
  photo_upload_failed: "Photo upload failed. Check your connection and retry; the photo was not saved.",
  gallery_note: "One gallery for the job: set-out, progress and finished rooms.",
  no_photos: "No photos yet. Camera or Upload adds the first.",
  scope_by_room: "Scope by room",
  skirting_scope: "Skirting scope",
  skirting_supply_install: "Artemis supplies and installs",
  skirting_install_only: "Customer supplies; Artemis installs",
  skirting_profile_pending: "Profile and height must be confirmed before ordering",
  skirting_measurement_pending: "Approved net length is missing — ask the office before ordering or installing",
  skirting_measured: "Measured net length: {length} LM",
  total: "Total",
  done_at: "Done {time}",
  blocked_setout: "Needs the set-out check",
  blocked_photo: "Needs at least one photo",
  site_record_open: "Site record & measurements",
  lay_open: "Saved plank lay diagram",
  lay_title: "Plank lay diagram",
  lay_empty: "No saved simple plank lay diagram is available for this job yet. Check the mobile section has been measured and saved.",
  lay_board: "Board {length} × {width} mm",
  lay_start_cut: "Start cut",
  lay_full: "Full plank",
  lay_end_cut: "End cut",
  lay_start_wall: "START WALL",
  lay_end_wall: "END WALL",
  lay_row_length: "Row {row}, measured {length} mm",
  lay_row_width: "Row {row} · {width} mm wide",
  lay_check_on_site: "Row {row}: measured; resolve the tongue-safe cut on site.",
  lay_note: "Lengths at right are wall face to wall face in mm. Leave {gap} mm expansion gap at each end. Cut the START piece at its wall-facing end and retain its factory joining edge. Confirm cuts and starting wall on site.",
  lay_pair_title: "One cut, two rows",
  lay_pair_note: "Cut each listed board once. Put both cut faces at the walls and keep the factory joining ends toward the next plank.",
  lay_pair_board: "Board {board}",
  lay_kerf: "kerf",
  site_record_title: "Site record",
  site_record_measurements: "Installation verification",
  site_record_wall: "Wall face to wall face (mm)",
  site_record_trim: "Trim run (mm)",
  cut_runner_title: "Cut Runner",
  cut_runner_intro: "Length only. Take repeated wall-to-wall laser shots along a plank row; the target keeps {gap} mm back from each wall face.",
  cut_runner_plank: "Plank length (mm)",
  cut_runner_site_yes: "This site suits pre-cutting",
  cut_runner_site_no: "Cut this site on site",
  cut_runner_row: "Row label",
  cut_runner_shots: "Laser shots, separated by commas",
  cut_runner_add: "Add row",
  cut_runner_ready: "Ready to pre-cut",
  cut_runner_verify: "Verify on site",
  cut_runner_site_only: "Site cut only",
  cut_runner_delete: "Delete row",
  cut_runner_transition: "Doorway transition",
  cut_runner_transition_help: "Measure from the actual laser endpoint to the transition. Shorter if the laser hit beyond it; longer if it stopped before it. Blank means no adjustment. Never add a recess already included in the reading. Keep {gap} mm end clearance.",
  cut_runner_adjustment: "Endpoint adjustment",
  cut_runner_adjustment_mm: "Adjustment in mm",
  cut_runner_longer: "Longer",
  cut_runner_shorter: "Shorter",
  cut_runner_offset_invalid: "{row}: enter a valid doorway offset in mm.",
  cut_runner_end_left: "Left end",
  cut_runner_right: "Right end",
  cut_runner_top: "Top end",
  cut_runner_bottom: "Bottom end",
  cut_runner_row_default: "Row {n}",
  cut_runner_source_new: "cut from new board",
  cut_runner_source_reused: "reuse offcut",
  cut_runner_source_full: "full board",
  cut_runner_offcuts: "Reusable offcuts",
  cut_runner_reason_site: "This site is marked for cutting on site.",
  cut_runner_reason_fixed: "Choose a product with a fixed plank length.",
  cut_runner_reason_empty: "{row}: no laser length.",
  cut_runner_reason_second: "{row}: take a second laser shot before pre-cutting.",
  cut_runner_reason_spread: "{row}: repeated shots differ by {spread} mm; measure again.",
  cut_runner_left: "left",
  site_record_note: "Installer note",
  site_record_offline: "Offline · saved on this device",
  site_record_pending: "Waiting to sync",
  site_record_local: "Unsaved device changes",
  site_record_synced: "Saved with the office",
  site_record_saved_local: "Change saved on this device.",
  site_record_syncing: "Saving with the office…",
  site_record_retry_waiting: "The reply was interrupted. The same save is waiting to retry safely.",
  site_record_queued: "Saved on this device. It will sync when the connection returns.",
  site_record_same_retry: "Retry keeps the same save identity; it cannot create a duplicate.",
  site_record_save: "Save revision",
  site_record_save_device: "Save on this device",
  site_record_denied: "Site record unavailable",
  site_record_denied_body: "This work record is not assigned to your installer account.",
  site_record_empty: "No shared site record has been published for this job.",
  site_record_failure_test: "Review controls · local build",
  site_record_go_offline: "Simulate offline",
  site_record_reconnect: "Reconnect",
  site_record_drop_reply: "Interrupt next save reply",
  // ppe chips
  ppe_hivis: "Hi-Vis",
  ppe_steelcap: "Steel cap",
  ppe_hardhat: "Hard hat",
  ppe_gloves: "Gloves",
  ppe_eyes: "Eye protection",
  ppe_ears: "Hearing protection",
  ppe_mask: "Dust mask",
  // set-out
  setout_title: "Set-out check",
  setout_intro: "Before the first row goes down. Every line needs a tick; the day cannot be completed without this check.",
  gate_moisture: "Subfloor moisture under 75 % RH",
  gate_level: "Level within 3 mm over 3 m",
  gate_gap: "10 mm expansion gap marked on all walls",
  gate_direction: "First row direction matches the drawing",
  gate_jambs: "Door jambs undercut",
  setout_submit: "Submit set-out · {n} of 5 done",
  setout_note: "Goes to the office. You cannot change the schedule from here.",
  setout_submitted: "Set-out check · submitted {time}",
  setout_not_submitted: "Set-out check · not submitted · needs 5 of 5",
  setout_photos: "Set-out photos",
  // completion
  completion_title: "Day complete · {name}",
  todays_photos: "Today's photos · finished rooms",
  one_per_room: "at least one per room",
  notes: "Notes",
  notes_placeholder: "Anything the office should know: leftovers, shims, what is left for tomorrow.",
  day_card: "Day {n} · {date}",
  need_photos_ok: "Today's photos · {n}",
  need_photos_no: "Today's photos · none yet",
  cannot_submit: "Cannot be submitted without today's photos and the set-out check.",
  scope_completed: "Completed assigned scope",
  scope_missing: "This day has no assigned scope. Ask the office to correct the schedule.",
  scope_tick_help: "Tick the assigned scope to complete this day. Photos and notes are optional.",
  photos_optional: "Optional",
  day_done_sent: "Day {n} complete · sent {time}",
  pm_walks: "walks through with {customer}",
  signature_opens: "Signature · opens after all days are complete",
  rooms_clear: "Rooms clear of tools and offcuts",
  office_review:"Office review",
  office_review_separate:"Day reports record the installer's work. The office confirms sign-off and invoicing separately.",
  days_reported_office_separate:"All recorded days have completion reports. Office sign-off and invoicing are not confirmed by these reports.",
  published_statements:"Published statements",
  statement_snapshot:"Saved statement · as published {date}. Later payments and adjustments may not be included.",
  statement_unavailable:"Statements could not be loaded. Retry or contact the office; this does not mean nothing is owed.",
  statement_none_published:"No statement has been published here for your company. Contact the office about fees or payment status.",
  statement_choose:"Statement period",
  statement_fees:"Fees at publication",
  statement_paid_snapshot:"Payments recorded at publication",
  statement_outstanding_snapshot:"Outstanding at publication",
  statement_incomplete:"Some job fees were unconfirmed when this statement was published. Totals are incomplete.",
  amount_unconfirmed:"Unconfirmed",
  statement_rates_separate:"Agreed pricing and payment status are confirmed separately with the office.",
  retry:"Retry",
  when_pm_signs_ext: "Office sign-off, invoicing and payment status are confirmed separately.",
  when_pm_signs_int: "Office sign-off and invoicing are confirmed separately.",
  // statement
  statement_title: "What I am owed",
  viewing_as: "Viewing as",
  fixture_note: "review fixture, not a control",
  view_external: "external",
  view_internal: "internal",
  owed_to: "owed to {party} · {month} to date",
  paid_on: "{amount} paid {date}",
  pending_signoff: "{amount} pending sign-off",
  lines: "Lines",
  your_labour_only: "your labour only",
  col_job: "Job",
  col_work: "Work",
  col_amount: "Amount",
  col_balance: "Balance",
  col_state: "State",
  col_days: "Days",
  state_paid: "Paid",
  state_pending: "Pending sign-off",
  statements: "Statements",
  draft: "draft",
  rate_card: "Rate card",
  rate_hybrid: "Hybrid / laminate",
  rate_carpet: "Carpet",
  rate_stairs: "Stairs",
  rate_removal: "Removal",
  per_step: "per step",
  agreed: "agreed {when}",
  rates_never: "Other parties' rates and Artemis pricing are never shown here.",
  int_stat: "{n} Team Artemis jobs this fortnight · no rates, no amounts",
  all_crew_jobs: "All crew jobs",
  no_owed_title: "No Owed tab for internal crews",
  no_owed_body: "Statements and rates exist only for external parties; Team Artemis is paid through payroll.",
  statement_empty: "No lines yet. Your first signed-off job appears here.",
  // answers from the office (never the process)
  drawing_not_published: "No set-out drawing has been published for this work order.",
  photos_load_failed:"Photos could not be loaded. Reopen the job or contact the office.",
    recorded_day_required: "No valid assigned installation day is recorded. Ask the office to check the schedule.",
  saved_report_unavailable: "Saved work status is unavailable. Ask the office to check your installer access.",
  team_title:"Company team",team_sub:"{company} · people and assigned work",team_manager_only:"Only an active company manager can manage people and assignments.",team_add_title:"Add a worker",team_name:"Name",team_mobile:"Mobile",team_email:"Email",team_language:"Language",team_add_note:"This prepares one personal account inside your company. It does not grant Artemis staff access and sends nothing yet.",team_prepare_access:"Prepare access",team_access_prepared:"Access requested. The account is being prepared; no invitation has been sent.",team_waiting:"Waiting for approval",team_restored:"Worker access restored.",team_deactivated:"Worker access revoked immediately.",team_assignment_saved:"Worker assignment saved.",team_invite_queued:"Invitation approved and queued for delivery.",team_invite_review:"Review invitation",team_invite_irreversible:"Sending reaches a real person and cannot be recalled. Check the recipient and full message above.",team_send_invite:"Approve & send",team_people:"People",team_role_manager:"manager",team_role_worker:"worker",team_active:"active",team_deactivate:"Deactivate",team_restore:"Restore",team_review_invite:"Review invitation",team_empty:"No people have been added yet.",team_allocate:"Assign people to company work",team_no_work:"No company installation days are ready for worker assignment.",team_save_assignment:"Save worker assignment",
  pricing_form_title:"Installation pricing",pricing_form_sub:"Select what your crew installs, then enter the rates you can quote now.",pricing_form_manager_only:"Only the company manager can submit pricing.",pricing_form_unavailable:"The pricing form is unavailable. Try again or contact Artemis.",pricing_form_latest:"Latest submission",pricing_form_approved:"approved",pricing_form_review:"submitted · awaiting office review",pricing_form_scope:"Capability scope",pricing_form_scope_help:"Tick every flooring type your crew can install. This controls suitable work; it does not approve a rate.",pricing_form_blank:"Not quoted yet",pricing_form_terms:"Terms",pricing_form_one_crew:"Minimum day · 1 crew ($)",pricing_form_two_crew:"Minimum day · 2 crews ($)",pricing_form_effective:"Rates effective from",pricing_form_notes:"Notes or exclusions",pricing_form_gst:"I confirm these rates exclude GST.",pricing_form_submit:"Submit pricing for review",pricing_form_saving:"Submitting…",pricing_form_submitted:"Pricing submitted. Artemis will review this exact version before using it.",pricing_form_waiting:"Waiting for approval",pricing_form_failed:"Pricing could not be submitted.",pricing_form_not_approval:"Submitting does not approve or activate these rates. Artemis keeps each version for audit.",
  pricing_form_daily_optional: "If no day minimum has been quoted, leave both day fields blank. Do not enter zero for an unknown rate.",
  saved_report_ready: "Installer access confirmed — ready to report work.",
  saved_work_accepted: "Work accepted — saved with the office.",
  saved_work_needs_acceptance: "Accept this installation before reporting work.",
  accept_this_work: "Accept this work",
  ans_executed: "Done · the office has it",
  ans_pending: "Waiting for office approval",
  ans_denied: "The office could not take this: {reason}",
  ans_failed: "Could not send · check your connection and try again",
  ans_server_failed: "The server rejected this day report",
  ans_session_failed: "Your sign-in changed. Sign in again, then retry.",
  ans_network_failed: "Could not reach the server. Check your connection and retry.",
  day_other_installer: "Assigned to {name}. Only that installer can report this day.",
  // ── my calendar ────────────────────────────────────────────────────────────
  // Jacky, 18 September: *"each crew should also have a subscription calendar that is generated, so
  // they can click and add it to calendar."* The crew sees the address and what it does; the office
  // issues it, so the empty state names the office rather than offering a button that would fail.
  cal_title: "My schedule in my calendar",
  cal_why: "Add this once and every job we book for you appears in your phone's calendar. It updates by itself — you do not need to add it again.",
  cal_add: "Add to my calendar",
  cal_copy: "Copy the link",
  cal_copied: "Link copied",
  cal_paste: "Or paste this into Apple or Outlook Calendar:",
  cal_none: "Your calendar link has not been set up yet — ask the office to send it.",
  cal_read: "Last updated from this link · {date}",
  cal_failed: "The calendar link is unavailable right now. Try again, or ask the office.",
} as const;

export type Key = keyof typeof en;

const zh: Record<Key, string> = {
  cal_title: "把排期加到我的日历",
  cal_why: "添加一次即可：我们为你安排的每一天都会自动出现在手机日历里。日历会自动更新，无需重复添加。",
  cal_add: "添加到我的日历",
  cal_copy: "复制链接",
  cal_copied: "已复制链接",
  cal_paste: "或把此链接粘贴到 Apple / Outlook 日历：",
  cal_none: "尚未为你生成日历链接——请联系办公室发送。",
  cal_read: "此链接最近更新 · {date}",
  cal_failed: "暂时无法获取日历链接，请重试或联系办公室。",
  payments_and_statements: "付款与结算单",
  recorded_payments: "已记录付款",
  fees_tab: "工程费用",
  fees_explainer: "办公室为每个工程确认的费用：各行明细、合计（不含 GST）及付款方式。除非您提出异议，视为已同意。",
  fees_unavailable: "暂时无法读取工程费用。请刷新或联系办公室。",
  fees_empty: "尚无已确认的工程费用。工程施工结清后，办公室会确认费用。",
  fee_agreed: "已同意",
  fee_variation_open: "已提出异议",
  fee_variation_accepted: "异议已接受——办公室正在更新费用",
  fee_variation_declined: "异议未被接受",
  fee_total: "合计 {amount}（不含 GST）",
  fee_paid_cash: "现金支付",
  fee_paid_transfer: "银行转账支付",
  fee_confirmed_on: "办公室确认于 {date}",
  fee_raise: "提出异议",
  fee_raise_help: "告诉办公室哪里不同。办公室回复前，费用保持已确认。",
  fee_note: "哪里不同",
  fee_proposed: "您认为应为的金额（可选，不含 GST）",
  fee_send: "发送给办公室",
  fee_sent: "已发送。办公室会在这里回复您。",
  fee_cancel: "取消",
  fee_your_note: "您的说明 · {date}",
  // Installer fee payments, per Job (25 Oct 2026)
  jobpay_title: "按工单的付款",
  jobpay_explainer: "办公室为您公司记录的每笔付款，对应所支付的工单：现金或银行转账、日期，以及您开具发票时的发票编号。金额不含 GST。",
  jobpay_unavailable: "您的付款暂时无法读取。请刷新或联系办公室。",
  jobpay_empty: "尚无按工单记录的付款。",
  jobpay_amount: "{amount}（不含 GST）",
  jobpay_paid_on: "{date} 已付",
  jobpay_invoice: "发票 {number}",
  jobpay_cash: "现金",
  jobpay_transfer: "银行转账",
  payments_explainer: "办公室记录的付款，与已发布结算单分开显示。没有收款确认不代表尚未付款。这些记录并非当前应付余额，也不保证银行已完成结算。",
  payments_refresh: "刷新记录",
  payments_unavailable: "付款记录暂不可用或需要办公室核查。请刷新或联系办公室。这不代表没有应付款。",
  payments_empty: "您公司暂无可用付款记录。这不能确定应付金额；如有付款遗漏，请联系办公室。",
  payment_recorded_date: "付款记录 · {date}",
  payment_reference: "参考号：{reference}",
  payment_reference_missing: "未记录参考号",
  payment_receipt_confirmed: "已记录收款确认 · {date}",
  payment_receipt_unconfirmed: "尚未记录收款确认",
  payment_allocations: "分配至工程",
  payment_no_allocations: "未记录工程分配",
  payment_unallocated: "尚未分配至工程：{amount}",

  role_external: "外部安装团队",
  role_internal: "内部团队",
  lang_toggle: "EN",
  tab_today: "今天",
  tab_jobs: "我的工作",
  tab_wo: "工单",
  tab_owed: "结算",
  tab_owed_external_only: "结算 · 仅外部团队",
  tab_team: "团队",
  tab_pricing: "报价",
  mock_chip: "示例数据",
  loading: "加载中…",
  signing_in: "正在登录…",
  not_here: "没有这个页面",
  no_such_page: "页面不存在。",
  sign_out: "退出登录",
  back: "返回",
  send: "发送",
  open: "打开",
  signin_title: "Artemis 安装团队门户",
  signin_body: "用办公室登记的邮箱登录。我们会发一条链接，在这部手机上点开即可。",
  signin_email: "邮箱",
  signin_send: "发送登录链接",
  signin_sent: "链接已发送 · 请在这部手机上查看邮箱。",
  signin_failed: "链接发送失败：{error}",
  signin_phone_body: "用办公室登记的手机号登录，我们会发送短信验证码。",
  signin_phone: "手机号",
  signin_send_code: "发送验证码",
  signin_code_sent: "验证码已发送至 {phone} · 五分钟内有效。",
  signin_code: "短信验证码",
  signin_verify: "登录",
  signin_change_number: "更换手机号",
  signin_welcome: "已登录，本机将保持登录状态。",
  signin_use_email: "改用邮箱链接登录",
  signin_staff_sso: "Artemis 员工 · Microsoft 登录",
  signin_staff_sso_note: "Jacky 及 Artemis 员工使用，不会发送邮件链接。",
  signin_use_phone: "改回手机号登录",
  title_today: "今天",
  sub_today: "{date} · {n} 个工地 · 第 {d} 天，共 {m} 天",
  sub_today_many: "{date} · {n} 个工地",
  sub_today_none: "{date} · 今天没有工地",
  no_job_today: "今天没有工地",
  work_load_failed: "无法读取分配给您的工作，请重试。",
  retry_work: "重试",
  next_job: "下一个：{name} · {date}",
  day_n_of_m: "第 {n}/{m} 天",
  stairs: "级楼梯",
  start_at: "{time} 开工",
  start_time_unset: "开工时间待确认",
  materials_status_unrecorded: "材料送达状态尚未记录",
  materials_since: "材料已于 {date} 送达工地",
  materials_not_yet: "材料尚未送达",
  btn_work_order: "工单",
  assigned_day: "已分配安装日",
  onsite_access_needed: "此账号负责管理公司工单。请先分配现场安装人员，再提交完工报告。",
  btn_set_out: "放样检查",
  btn_day_complete: "今日完工",
  biglink_complete: "今日完工 · 勾选分配的施工范围",
  new_job_offered: "新工地邀约",
  tomorrow: "明天",
  tomorrow_none: "明天没有安排",
  day_n: "第 {n} 天",
  offer_from: "Artemis 办公室",
  offer_answer_by: "请在{when}前答复",
  offer_what: "会发生什么",
  offer_why: "为什么找你",
  offer_options: "选项",
  offer_if_no: "如果你拒绝",
  offer_undo: "撤销",
  undo_reversible: "可以撤销",
  undo_with_effort: "撤销需要联系办公室",
  undo_irreversible: "无法撤销",
  verb_accept: "接受",
  verb_ask: "提问…",
  verb_decline: "拒绝",
  ask_label: "向办公室提问",
  ask_placeholder: "你的问题会发给办公室，他们会在这里回复。",
  decline_label: "拒绝原因（可选）",
  decline_placeholder: "给办公室的一句话。",
  offer_sending: "发送中…",
  offer_accepted: "已接受 · 已加入你的工作列表",
  offer_asked: "问题已发给办公室",
  offer_declined: "已拒绝 · 办公室会邀约其他团队",
  offer_failed: "发送失败 · 请重试",
  offer_expired: "此邀约已关闭",
  jobs_scope_external: "仅显示 {party} 的工地。",
  jobs_scope_internal: "Team Artemis · 所有团队工地，不显示费率。",
  group_upcoming: "即将开工",
  group_completion_to_check: "待核对完工",
  group_done: "已完成",
  empty_group: "暂无",
  pill_installing: "施工中",
  pill_accepted: "已接受",
  pill_offered: "已邀约 · {when}前答复",
  pill_scheduled: "已排期",
  pill_done: "{date} 已验收",
  pill_install_complete: "安装已完成",
  pill_completion_to_check: "核对完工",
  pill_day_today: "今天第 {n} 天",
  pill_day_complete: "第 {n} 天已完工",
  wo_title: "工单 · {name}",
  wo_no_job: "暂时没有可显示的工单。",
  wo_not_published: "工单详情暂时无法读取。下方仍显示已分配的工地和日期。",
  site: "工地",
  open_maps: "打开地图",
  days: "工期",
  parking: "停车与卸货",
  access: "进入方式",
  ppe: "防护装备",
  office: "办公室",
  notes_office: "办公室备注",
  materials: "本项目所需材料",
  supplier_pickups: "供应商提货",
  pickup_number: "提货时出示此编号",
  pickup_awaiting_email: "供应商提货编号尚待邮件确认。提货前请联系办公室。",
  delivered: "{date} 已送达",
  not_delivered: "尚未送达",
  your_labour: "你的工费",
  party_only: "仅 {party}",
  labour_total: "合计",
  on_statement: "验收签字后计入结算单",
  internal_no_rates: "Team Artemis 团队不会看到任何费率或金额。",
  setout_drawing: "放样图",
  setout_caption: "铺设方向 · 首排 · 来自 {date} 的测量",
  start_row: "首排 · 10 mm 伸缩缝",
  days_caption: "每天完工后提交，最后由项目经理签字",
  pm_signoff: "项目经理签字",
  pm_after_all: "全部完工后",
  pm_detail: "{pm} · 第 {n} 天之后 · 每个房间的照片",
  photos: "现场照片",
  camera: "相机",
  upload: "上传",
  uploading: "上传中…",
  photo_prepare_failed: "这张照片无法在此手机上压缩。请换一张照片或相机格式。",
  photo_too_large: "这张照片无法压缩到 1 MB 以下。请换一张照片。",
  photo_format_failed: "照片格式被拒绝。请尝试 JPEG 或重新拍照。",
  photo_access_failed: "照片上传权限已过期。请重新登录后再试。",
  photo_upload_failed: "照片上传失败。请检查网络后重试；照片尚未保存。",
  gallery_note: "本工地只有一个相册：放样、进度和完工房间。",
  no_photos: "还没有照片。用相机或上传添加第一张。",
  scope_by_room: "各房间范围",
  skirting_scope: "踢脚线范围",
  skirting_supply_install: "Artemis 供应并安装",
  skirting_install_only: "客户自购；Artemis 安装",
  skirting_profile_pending: "订货前必须确认款式与高度",
  skirting_measurement_pending: "尚无获批净长度——订货或安装前请联系办公室",
  skirting_measured: "实测净长度：{length} 延米",
  total: "合计",
  done_at: "{time} 已完工",
  blocked_setout: "需要先完成放样检查",
  blocked_photo: "至少需要一张照片",
  site_record_open: "现场记录与测量",
  lay_open: "已保存的地板铺排图",
  lay_title: "地板铺排图",
  lay_empty: "此工作尚无已保存的简易地板铺排图。请确认手机上的区域已测量并保存。",
  lay_board: "地板规格 {length} × {width} 毫米",
  lay_start_cut: "起始切板",
  lay_full: "整板",
  lay_end_cut: "末端切板",
  lay_start_wall: "起始墙",
  lay_end_wall: "末端墙",
  lay_row_length: "第 {row} 排，实测 {length} 毫米",
  lay_row_width: "第 {row} 排 · 宽 {width} 毫米",
  lay_check_on_site: "第 {row} 排：已测量，请在现场确认保留榫口的切法。",
  lay_note: "右侧长度为墙面到墙面的实测毫米数。两端各留 {gap} 毫米伸缩缝。起始板从靠墙端切除，保留朝向下一块板的原厂拼接边。请在现场核对切板和起始墙。",
  lay_pair_title: "一刀切出两排的板块",
  lay_pair_note: "每块列出的整板只切一次。两块的切口朝墙，原厂拼接端朝下一块板。",
  lay_pair_board: "板材 {board}",
  lay_kerf: "锯缝",
  site_record_title: "现场记录",
  site_record_measurements: "安装复核",
  site_record_wall: "墙面到墙面（毫米）",
  site_record_trim: "收口条长度（毫米）",
  cut_runner_title: "切板助手",
  cut_runner_intro: "只计算长度。沿板中心连续测量墙到墙；成品两边各预留 {gap} 毫米。",
  cut_runner_plank: "每块板长度（毫米）",
  cut_runner_site_yes: "此工地适合预切",
  cut_runner_site_no: "此工地现场切割",
  cut_runner_row: "排号",
  cut_runner_shots: "多次激光尺寸，用逗号分隔",
  cut_runner_add: "新增一排",
  cut_runner_ready: "可以预切",
  cut_runner_verify: "现场复核",
  cut_runner_site_only: "只可现场切割",
  cut_runner_delete: "删除此排",
  cut_runner_transition: "门口收口",
  cut_runner_transition_help: "从激光实际终点量到收口线。激光超出收口线则选缩短，未到收口线则选加长。留空表示不调整；不要重复添加已计入读数的门框深度。末端保留 {gap} 毫米间隙。",
  cut_runner_adjustment: "终点调整",
  cut_runner_adjustment_mm: "调整毫米数",
  cut_runner_longer: "加长",
  cut_runner_shorter: "缩短",
  cut_runner_offset_invalid: "{row}：请输入有效的门口偏移距离（毫米）。",
  cut_runner_end_left: "左端",
  cut_runner_right: "右端",
  cut_runner_top: "上端",
  cut_runner_bottom: "下端",
  cut_runner_row_default: "第 {n} 排",
  cut_runner_source_new: "新板切割",
  cut_runner_source_reused: "使用余料",
  cut_runner_source_full: "整板",
  cut_runner_offcuts: "可复用余料",
  cut_runner_reason_site: "此工地已标记为现场切割。",
  cut_runner_reason_fixed: "请选择长度固定的地板产品。",
  cut_runner_reason_empty: "{row}：没有激光长度。",
  cut_runner_reason_second: "{row}：预切前请再测一次激光。",
  cut_runner_reason_spread: "{row}：重复尺寸相差 {spread} 毫米，请重新测量。",
  cut_runner_left: "剩余",
  site_record_note: "安装人员备注",
  site_record_offline: "离线 · 已保存到本机",
  site_record_pending: "等待同步",
  site_record_local: "本机有未保存更改",
  site_record_synced: "办公室已保存",
  site_record_saved_local: "更改已保存到本机。",
  site_record_syncing: "正在保存到办公室…",
  site_record_retry_waiting: "回复中断。同一次保存正等待安全重试。",
  site_record_queued: "已保存到本机，网络恢复后会自动同步。",
  site_record_same_retry: "重试沿用同一保存标识，不会产生重复版本。",
  site_record_save: "保存版本",
  site_record_save_device: "保存到本机",
  site_record_denied: "无法打开现场记录",
  site_record_denied_body: "此工作记录未分配给您的安装团队。",
  site_record_empty: "此工地尚未发布共享现场记录。",
  site_record_failure_test: "评审控制 · 本地版本",
  site_record_go_offline: "模拟离线",
  site_record_reconnect: "重新连接",
  site_record_drop_reply: "中断下一次保存回复",
  ppe_hivis: "反光衣",
  ppe_steelcap: "钢头鞋",
  ppe_hardhat: "安全帽",
  ppe_gloves: "手套",
  ppe_eyes: "护目镜",
  ppe_ears: "护耳",
  ppe_mask: "防尘口罩",
  setout_title: "放样检查",
  setout_intro: "第一排铺设前完成。每项都需要打勾；未完成此检查无法提交今日完工。",
  gate_moisture: "基层湿度低于 75 % RH",
  gate_level: "3 m 内平整度 3 mm 以内",
  gate_gap: "四周 10 mm 伸缩缝已标记",
  gate_direction: "首排方向与图纸一致",
  gate_jambs: "门框已下切",
  setout_submit: "提交放样检查 · 已完成 {n}/5",
  setout_note: "发送给办公室。此处无法修改排期。",
  setout_submitted: "放样检查 · {time} 已提交",
  setout_not_submitted: "放样检查 · 未提交 · 需完成 5/5",
  setout_photos: "放样照片",
  completion_title: "今日完工 · {name}",
  todays_photos: "今日照片 · 完工房间",
  one_per_room: "每个房间至少一张",
  notes: "备注",
  notes_placeholder: "需要办公室知道的事：余料、垫片、明天剩下什么。",
  day_card: "第 {n} 天 · {date}",
  need_photos_ok: "今日照片 · {n} 张",
  need_photos_no: "今日照片 · 还没有",
  cannot_submit: "没有今日照片和放样检查无法提交。",
  scope_completed: "已完成分配的施工范围",
  scope_missing: "此施工日没有分配施工范围，请办公室更正排期。",
  scope_tick_help: "勾选已完成的施工范围即可提交当天完工。照片和备注可选填。",
  photos_optional: "可选",
  day_done_sent: "第 {n} 天已完工 · {time} 已发送",
  pm_walks: "与 {customer} 一起巡查",
  signature_opens: "签名 · 全部完工后开放",
  rooms_clear: "房间已清理工具和余料",
  office_review:"办公室复核",
  office_review_separate:"每日完工报告记录安装工作。办公室另行确认验收和开票。",
  days_reported_office_separate:"所有排定日期均有完工报告。这些报告不代表办公室已验收或开票。",
  published_statements:"已发布结算单",
  statement_snapshot:"结算快照 · 发布于 {date}。之后的付款和调整可能未包含。",
  statement_unavailable:"结算单无法加载。请重试或联系办公室；这不代表没有应付款。",
  statement_none_published:"此处尚未发布你公司的结算单。请向办公室确认费用和付款状态。",
  statement_choose:"结算期间",
  statement_fees:"发布时费用",
  statement_paid_snapshot:"发布时已记录付款",
  statement_outstanding_snapshot:"发布时未付余额",
  statement_incomplete:"发布时部分工地费用尚未确认，合计不完整。",
  amount_unconfirmed:"待确认",
  statement_rates_separate:"约定价格和付款状态请另向办公室确认。",
  retry:"重试",
  when_pm_signs_ext: "办公室另行确认验收、开票和付款状态。",
  when_pm_signs_int: "办公室另行确认验收和开票。",
  statement_title: "应付结算",
  viewing_as: "查看身份",
  fixture_note: "评审用开关，不是产品控件",
  view_external: "外部团队",
  view_internal: "内部团队",
  owed_to: "应付 {party} · {month}至今",
  paid_on: "{date} 已付 {amount}",
  pending_signoff: "{amount} 待验收",
  lines: "明细",
  your_labour_only: "仅显示你的工费",
  col_job: "工地",
  col_work: "工作",
  col_amount: "金额",
  col_balance: "余额",
  col_state: "状态",
  col_days: "工期",
  state_paid: "已付",
  state_pending: "待验收",
  statements: "结算单",
  draft: "草稿",
  rate_card: "费率卡",
  rate_hybrid: "复合 / 强化",
  rate_carpet: "地毯",
  rate_stairs: "楼梯",
  rate_removal: "拆除",
  per_step: "每级",
  agreed: "{when}约定",
  rates_never: "此处不显示其他团队的费率或 Artemis 的售价。",
  int_stat: "Team Artemis 两周内 {n} 个工地 · 不显示费率或金额",
  all_crew_jobs: "所有团队工地",
  no_owed_title: "内部团队没有“结算”页",
  no_owed_body: "结算单和费率仅对外部团队显示；Team Artemis 通过工资发放。",
  statement_empty: "还没有明细。第一个验收签字的工地会显示在这里。",
  drawing_not_published: "此工作单尚未发布放样图。",
  photos_load_failed:"照片无法加载。请重新打开任务或联系办公室。",
    recorded_day_required: "没有有效的已分配安装日。请办公室检查排期。",
  saved_report_unavailable: "无法读取已保存的工作状态。请办公室检查您的安装人员权限。",
  team_title:"公司团队",team_sub:"{company} · 人员与已分配工作",team_manager_only:"只有有效的公司管理员可以管理员工和工作分配。",team_add_title:"添加员工",team_name:"姓名",team_mobile:"手机号",team_email:"邮箱",team_language:"语言",team_add_note:"这会在贵公司内准备一个独立账号，不会授予 Artemis 员工权限，也不会立即发送邀请。",team_prepare_access:"准备账号",team_access_prepared:"账号申请已记录，正在准备中；尚未发送邀请。",team_waiting:"等待批准",team_restored:"员工访问已恢复。",team_deactivated:"员工访问已立即撤销。",team_assignment_saved:"员工分配已保存。",team_invite_queued:"邀请已批准并排队发送。",team_invite_review:"检查邀请",team_invite_irreversible:"发送后会联系真实人员且无法撤回。请检查以上收件人和完整内容。",team_send_invite:"批准并发送",team_people:"人员",team_role_manager:"管理员",team_role_worker:"员工",team_active:"有效",team_deactivate:"停用",team_restore:"恢复",team_review_invite:"检查邀请",team_empty:"尚未添加人员。",team_allocate:"把人员分配到公司工作",team_no_work:"目前没有可分配员工的公司安装日。",team_save_assignment:"保存员工分配",
  pricing_form_title:"安装服务报价",pricing_form_sub:"先选择团队可安装的项目，再填写目前可以报价的费率。",pricing_form_manager_only:"只有公司管理员可以提交报价。",pricing_form_unavailable:"报价表暂时无法使用，请重试或联系 Artemis。",pricing_form_latest:"最近提交",pricing_form_approved:"已批准",pricing_form_review:"已提交 · 等待办公室审核",pricing_form_scope:"施工能力范围",pricing_form_scope_help:"勾选团队能够安装的所有地板类型。此范围用于匹配工作，不代表费率已批准。",pricing_form_blank:"尚未报价",pricing_form_terms:"条款",pricing_form_one_crew:"一天最低收费 · 1组（$）",pricing_form_two_crew:"一天最低收费 · 2组（$）",pricing_form_effective:"费率生效日期",pricing_form_notes:"备注或不包含项目",pricing_form_gst:"我确认以上费率未含 GST。",pricing_form_submit:"提交报价供审核",pricing_form_saving:"正在提交…",pricing_form_submitted:"报价已提交。Artemis 审核此版本后才会使用。",pricing_form_waiting:"等待批准",pricing_form_failed:"报价提交失败。",pricing_form_not_approval:"提交不等于批准或启用费率。Artemis 会保留每个版本以供审核。",
  pricing_form_daily_optional: "如果尚未提供每日最低收费，请将两项都留空；不要用零代替未知费率。",
  saved_report_ready: "安装人员权限已确认，可以提交工作报告。",
  saved_work_accepted: "已接受工作，办公室已保存。",
  saved_work_needs_acceptance: "请先接受此安装任务，再提交工作报告。",
  accept_this_work: "接受此工作",
  ans_executed: "已完成 · 办公室已收到",
  ans_pending: "等待办公室批准",
  ans_denied: "办公室无法接受：{reason}",
  ans_failed: "发送失败 · 请检查网络后重试",
  ans_server_failed: "服务器拒绝了当天完工报告",
  ans_session_failed: "登录状态已更改。请重新登录后重试。",
  ans_network_failed: "无法连接服务器。请检查网络后重试。",
  day_other_installer: "已分配给 {name}，只有该安装人员可以提交当天完工。",
};

const dict: Record<Lang, Record<Key, string>> = { en, zh };

/** Translate one of the installer's keys. Kept as the module's own export because
 *  components and the PPE mapping call it; the work is the shared mechanism's. */
export function translate(lang: Lang, key: Key, vars?: Vars): string {
  return translateIn(dict, lang, key, vars);
}

/** PPE labels arrive from the office as English chips; translate the known ones, show the rest as sent. */
const PPE: Record<string, Key> = { "hi-vis": "ppe_hivis", hivis: "ppe_hivis", "steel cap": "ppe_steelcap", "steel caps": "ppe_steelcap", "hard hat": "ppe_hardhat", gloves: "ppe_gloves", "eye protection": "ppe_eyes", "safety glasses": "ppe_eyes", "hearing protection": "ppe_ears", "dust mask": "ppe_mask" };
export function ppeWord(lang: Lang, label: string): string {
  const k = PPE[label.trim().toLowerCase()];
  return k ? translate(lang, k) : label;
}

interface LangCtx { lang: Lang; setLang: (l: Lang) => void; toggle: () => void; t: (key: Key, vars?: Vars) => string }
export const LangContext = createContext<LangCtx>({ lang: "en", setLang: () => {}, toggle: () => {}, t: (k, v) => translate("en", k, v) });

/** The shared hook, bound to this face's dictionary and storage key. */
export function useLangState(): LangCtx {
  return useSharedLangState(KEY, dict);
}

export const useT = () => useContext(LangContext);
